import { ORGANIZATION, SITE_NAME, SITE_URL } from "./siteConfig"

/** Stable @id values so the graph nodes can reference each other. */
export const ORGANIZATION_ID = `${SITE_URL}/#organization`
export const WEBSITE_ID = `${SITE_URL}/#website`

export type JsonLdNode = Record<string, unknown>

export function organizationSchema(): JsonLdNode {
  return {
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name: ORGANIZATION.name,
    legalName: ORGANIZATION.legalName,
    alternateName: ORGANIZATION.alternateName,
    url: `${SITE_URL}/`,
    logo: {
      "@type": "ImageObject",
      url: ORGANIZATION.logo,
    },
    image: ORGANIZATION.logo,
    email: ORGANIZATION.email,
    telephone: ORGANIZATION.telephone,
    slogan: ORGANIZATION.slogan,
    description:
      "Skinuvita bietet ärztlich begleitete UVB-Lichttherapie für zuhause bei Psoriasis (Schuppenflechte), " +
      "atopischer Dermatitis (Neurodermitis) und weiteren chronisch-entzündlichen Hauterkrankungen.",
    address: {
      "@type": "PostalAddress",
      streetAddress: ORGANIZATION.street,
      postalCode: ORGANIZATION.postalCode,
      addressLocality: ORGANIZATION.city,
      addressCountry: ORGANIZATION.country,
    },
    ...(ORGANIZATION.foundingDate ? { foundingDate: ORGANIZATION.foundingDate } : {}),
    foundingLocation: {
      "@type": "Place",
      name: ORGANIZATION.city,
      address: {
        "@type": "PostalAddress",
        addressLocality: ORGANIZATION.city,
        addressCountry: ORGANIZATION.country,
      },
    },
    founder: ORGANIZATION.founders.map((name) => ({ "@type": "Person", name })),
    knowsAbout: [...ORGANIZATION.knowsAbout],
    award: ORGANIZATION.award,
    vatID: ORGANIZATION.vatId,
    identifier: {
      "@type": "PropertyValue",
      name: "Handelsregister",
      value: `${ORGANIZATION.registerCourt} ${ORGANIZATION.registerNumber}`,
    },
    areaServed: { "@type": "Country", name: "Deutschland" },
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "customer support",
        telephone: ORGANIZATION.telephone,
        email: ORGANIZATION.supportEmail,
        availableLanguage: ["de", "en"],
        areaServed: "DE",
      },
    ],
    sameAs: [...ORGANIZATION.sameAs],
  }
}

export function websiteSchema(): JsonLdNode {
  return {
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    url: `${SITE_URL}/`,
    name: SITE_NAME,
    alternateName: ORGANIZATION.legalName,
    description:
      "Informationen und Selbst-Check zur ärztlich begleiteten UVB-Lichttherapie für zuhause bei " +
      "Schuppenflechte und Neurodermitis.",
    inLanguage: ["de-DE", "en"],
    about: { "@id": ORGANIZATION_ID },
    keywords: [...ORGANIZATION.knowsAbout],
    publisher: { "@id": ORGANIZATION_ID },
    copyrightHolder: { "@id": ORGANIZATION_ID },
  }
}

export function webPageSchema(args: {
  url: string
  name: string
  description: string
  locale?: string
  primaryImage?: string
}): JsonLdNode {
  const node: JsonLdNode = {
    "@type": "WebPage",
    "@id": `${args.url}#webpage`,
    url: args.url,
    name: args.name,
    description: args.description,
    inLanguage: args.locale === "en" ? "en" : "de-DE",
    isPartOf: { "@id": WEBSITE_ID },
    about: { "@id": ORGANIZATION_ID },
    publisher: { "@id": ORGANIZATION_ID },
  }

  if (args.primaryImage) {
    node.primaryImageOfPage = { "@type": "ImageObject", url: args.primaryImage }
  }

  return node
}

export function breadcrumbSchema(items: { name: string; url: string }[]): JsonLdNode {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  }
}

export type FaqEntry = { question: string; answer: string }

/**
 * FAQPage requires a non-empty plain-text answer, so entries whose answer only
 * exists as JSX are dropped rather than emitted empty (an invalid block is
 * ignored wholesale by consumers).
 */
export function faqPageSchema(entries: FaqEntry[]): JsonLdNode | null {
  const usable = entries
    .map((entry) => ({ question: entry.question?.trim(), answer: entry.answer?.trim() }))
    .filter((entry) => entry.question && entry.answer)

  if (usable.length === 0) {
    return null
  }

  return {
    "@type": "FAQPage",
    mainEntity: usable.map((entry) => ({
      "@type": "Question",
      name: entry.question,
      acceptedAnswer: { "@type": "Answer", text: entry.answer },
    })),
  }
}

export function articleSchema(args: {
  url: string
  headline: string
  description: string
  image?: string
  datePublished?: string
  dateModified?: string
  authorName?: string
  section?: string
  wordCount?: number
}): JsonLdNode {
  const node: JsonLdNode = {
    "@type": "Article",
    "@id": `${args.url}#article`,
    headline: args.headline,
    description: args.description,
    mainEntityOfPage: args.url,
    url: args.url,
    inLanguage: "de-DE",
    isPartOf: { "@id": WEBSITE_ID },
    publisher: { "@id": ORGANIZATION_ID },
    author: args.authorName
      ? { "@type": "Person", name: args.authorName }
      : { "@id": ORGANIZATION_ID },
  }

  if (args.image) node.image = args.image
  if (args.datePublished) node.datePublished = args.datePublished
  if (args.dateModified) node.dateModified = args.dateModified
  if (args.section) node.articleSection = args.section
  if (args.wordCount) node.wordCount = args.wordCount

  return node
}

/** Wraps the page's nodes into a single @graph document. */
export function buildGraph(nodes: (JsonLdNode | null | undefined)[]): string {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@graph": nodes.filter(Boolean),
  })
}
